import React, { useState, useEffect, useCallback } from 'react';
import { collection, updateDoc, setDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import './AdminApprovalsView.css';
import './AdminMoreDetailPages.css';
import StatCards from './StatCards';
import { scopeForAdmin, adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

function AdminApprovalsView({ adminData }) {
  const [requests, setRequests] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  const normalizeStatus = (status) => {
    if (!status) return 'pending';
    return status.toLowerCase();
  };

  // School-scoped cache key builder — shared with AdminAnalyticsView so tabs
  // reuse each other's fetched collections.
  const ck = (p) => `${adminSchoolIds(adminData).join('|') || 'all'}:${p}`;

  const loadRequests = useCallback(async () => {
    console.log('🔥 AdminApprovals: Loading requests like iOS');
    try {
      const allRequests = [];
      const scopedClassDocs = scopeForAdmin(
        await dataCacheService.getDocs(ck('classes'), collection(db, 'classes')),
        adminData
      );
      console.log('🔥 AdminApprovals: Classes found', scopedClassDocs.length);

      await Promise.all(scopedClassDocs.map(async (classDoc) => {
        const classId = classDoc.id;
        const className = classDoc.name || classId;

        // Load students in this class for name lookup
        const studentsDocs = await dataCacheService.getDocs(ck(`classes/${classId}/students`), collection(db, 'classes', classId, 'students'));
        const studentsById = {};
        studentsDocs.forEach(data => {
          studentsById[data.id] = data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.id;
          // Also map by studentId if different
          if (data.studentId) {
            studentsById[data.studentId] = studentsById[data.id];
          }
        });

        const assignmentDocs = await dataCacheService.getDocs(ck(`classes/${classId}/assignments`), collection(db, 'classes', classId, 'assignments'));

        await Promise.all(assignmentDocs.map(async (assignmentDoc) => {
          const assignmentId = assignmentDoc.id;
          const assignmentName = assignmentDoc.title || assignmentId;
          const gradeDocs = await dataCacheService.getDocs(ck(`classes/${classId}/assignments/${assignmentId}/grades`), collection(db, 'classes', classId, 'assignments', assignmentId, 'grades'));

          await Promise.all(gradeDocs.map(async (gradeDoc) => {
            const gradeId = gradeDoc.id;
            const gradeStudentId = gradeDoc.studentId;
            const requestDocs = await dataCacheService.getDocs(ck(`classes/${classId}/assignments/${assignmentId}/grades/${gradeId}/changeRequests`), collection(db, 'classes', classId, 'assignments', assignmentId, 'grades', gradeId, 'changeRequests'));

            requestDocs.forEach(requestData => {
              const studentId = requestData.studentId || gradeStudentId;
              allRequests.push({
                id: requestData.id,
                ...requestData,
                classId,
                className,
                assignmentId,
                assignmentName,
                gradeId,
                gradeStudentId: studentId,
                studentName: studentsById[studentId] || studentId
              });
            });
          }));
        }));
      }));

      console.log('🔥 AdminApprovals: Requests loaded', allRequests.length, allRequests);
      setRequests(allRequests);
    } catch (error) {
      console.error('🔥 AdminApprovals: Error loading requests:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData?.schoolId]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const getFilteredRequests = () => {
    if (selectedFilter === 'all') return requests;
    return requests.filter(request => normalizeStatus(request.status) === selectedFilter);
  };

  const getPercentage = (score, maxScore) => {
    if (!maxScore || maxScore <= 0) return 0;
    return (score / maxScore) * 100;
  };

  const getLetterGrade = (percentage) => {
    if (percentage >= 90) return 'A';
    if (percentage >= 80) return 'B';
    if (percentage >= 70) return 'C';
    if (percentage >= 60) return 'D';
    return 'F';
  };

  const getStatusColor = (status) => {
    const normalized = normalizeStatus(status);
    const colors = {
      'pending': '#f59e0b',
      'approved': '#22c55e',
      'rejected': '#ef4444'
    };
    return colors[normalized] || '#666';
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
    return new Date(dateValue).toLocaleDateString();
  };

  const handleApprove = async (request) => {
    if (!confirm('Are you sure you want to approve this grade change?')) return false;
    try {
      const now = new Date().toISOString();
      // Update the request status - use UUID for internal references like iOS.
      // Ref is rebuilt from the path (cached request docs carry no live ref).
      const requestRef = doc(db, 'classes', request.classId, 'assignments', request.assignmentId, 'grades', request.gradeId, 'changeRequests', request.id);
      await updateDoc(requestRef, {
        status: 'Approved',
        reviewedBy: adminData?.uuid || adminData?.uid, // Fallback to uid if uuid not available
        reviewedAt: now,
        reviewComment: ''
      });

      // Apply the grade change to the actual grade document (like iOS)
      if (request.classId && request.assignmentId && request.gradeId) {
        const gradeRef = doc(db, 'classes', request.classId, 'assignments', request.assignmentId, 'grades', request.gradeId);
        await updateDoc(gradeRef, {
          score: request.newScore,
          maxScore: request.newMaxScore,
          feedback: request.newFeedback || '',
          gradedBy: adminData?.uuid || adminData?.uid, // Fallback to uid if uuid not available
          gradedAt: now
        });
      }

      // Keep the top-level mirror (used for badge counts) in sync — merge so it's a no-op when absent
      if (request.id) {
        await setDoc(doc(db, 'gradeChangeRequests', request.id), { status: 'approved' }, { merge: true });
      }

      dataCacheService.invalidatePath('gradeChangeRequests');
      dataCacheService.invalidatePath(`classes/${request.classId}/assignments/${request.assignmentId}/grades`);
      loadRequests();
      return true;
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Failed to approve request');
      return false;
    }
  };

  const handleReject = async (request) => {
    if (!confirm('Are you sure you want to reject this grade change?')) return false;
    try {
      const requestRef = doc(db, 'classes', request.classId, 'assignments', request.assignmentId, 'grades', request.gradeId, 'changeRequests', request.id);
      await updateDoc(requestRef, {
        status: 'Rejected',
        reviewedBy: adminData?.uuid || adminData?.uid, // Fallback to uid if uuid not available
        reviewedAt: new Date().toISOString(),
        reviewComment: ''
      });
      if (request.id) {
        await setDoc(doc(db, 'gradeChangeRequests', request.id), { status: 'rejected' }, { merge: true });
      }
      dataCacheService.invalidatePath('gradeChangeRequests');
      dataCacheService.invalidatePath(`classes/${request.classId}/assignments/${request.assignmentId}/grades`);
      loadRequests();
      return true;
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Failed to reject request');
      return false;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const filteredRequests = getFilteredRequests();
  const pendingCount = requests.filter(r => normalizeStatus(r.status) === 'pending').length;

  const apprStats = {
    p: requests.filter(r => normalizeStatus(r.status) === 'pending').length,
    a: requests.filter(r => normalizeStatus(r.status) === 'approved').length,
    r: requests.filter(r => normalizeStatus(r.status) === 'rejected').length,
    students: new Set(requests.map(r => r.studentId || r.studentName).filter(Boolean)).size,
    classes: new Set(requests.map(r => r.classId || r.className).filter(Boolean)).size
  };

  // === Full-screen request detail page ===
  if (selectedRequest) {
    const r = selectedRequest;
    const origPct = getPercentage(r.originalScore, r.originalMaxScore);
    const newPct = getPercentage(r.newScore, r.newMaxScore);
    const status = normalizeStatus(r.status);
    const teacherLabel = r.teacherName || r.requestedByName || r.requestedBy || r.teacherId || null;
    console.log('🔥 AdminApprovals: Showing detail for request', r.id, r);
    return (
      <div className="admin-approvals-view detail-page">
        <button className="detail-back-button" onClick={() => setSelectedRequest(null)}>
          ◄ Back to Approvals
        </button>
        <div className="detail-card">
          <div className="detail-header">
            <div>
              <h2>Grade Change Request</h2>
              <p className="detail-subtitle">{r.assignmentName || 'Assignment'} — {r.className || 'N/A'}</p>
            </div>
            <span
              className="detail-status-badge"
              style={{ background: `${getStatusColor(r.status)}20`, color: getStatusColor(r.status) }}
            >
              {status}
            </span>
          </div>

          <div className="detail-grid">
            <div className="detail-field">
              <span className="detail-label">Student</span>
              <span className="detail-value">{r.studentName || r.studentId || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Class</span>
              <span className="detail-value">{r.className || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Assignment</span>
              <span className="detail-value">{r.assignmentName || 'N/A'}</span>
            </div>
            {(r.subjectName || r.subject) && (
              <div className="detail-field">
                <span className="detail-label">Subject</span>
                <span className="detail-value">{r.subjectName || r.subject}</span>
              </div>
            )}
            {teacherLabel && (
              <div className="detail-field">
                <span className="detail-label">Requested By (Teacher)</span>
                <span className="detail-value">{teacherLabel}</span>
              </div>
            )}
            <div className="detail-field">
              <span className="detail-label">Requested</span>
              <span className="detail-value">{formatDate(r.requestedAt)}</span>
            </div>
          </div>

          <div className="detail-section">
            <h4>Grade Change</h4>
            <div className="detail-item-list">
              <div className="detail-item-row">
                <div>
                  <div className="item-main">Original Grade</div>
                  <div className="item-sub">{r.originalScore}/{r.originalMaxScore}</div>
                </div>
                <span className="item-right" style={{ color: getLetterGrade(origPct) === 'F' ? '#dc2626' : '#111827' }}>
                  {origPct.toFixed(1)}% ({getLetterGrade(origPct)})
                </span>
              </div>
              <div className="detail-item-row">
                <div>
                  <div className="item-main">Requested Grade</div>
                  <div className="item-sub">{r.newScore}/{r.newMaxScore}</div>
                </div>
                <span className="item-right" style={{ color: '#16a34a' }}>
                  {newPct.toFixed(1)}% ({getLetterGrade(newPct)})
                </span>
              </div>
              {r.newFeedback && (
                <div className="detail-item-row">
                  <div>
                    <div className="item-main">New Feedback</div>
                    <div className="item-sub">{r.newFeedback}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h4>Reason</h4>
            <p className={`detail-text ${!r.reason ? 'empty' : ''}`}>{r.reason || 'No reason provided'}</p>
          </div>

          {status !== 'pending' && (
            <div className="detail-section">
              <h4>Review</h4>
              <div className="detail-grid">
                <div className="detail-field">
                  <span className="detail-label">Reviewed By</span>
                  <span className="detail-value">{r.reviewedBy || 'N/A'}</span>
                </div>
                <div className="detail-field">
                  <span className="detail-label">Reviewed At</span>
                  <span className="detail-value">{formatDate(r.reviewedAt)}</span>
                </div>
                {r.reviewComment && (
                  <div className="detail-field">
                    <span className="detail-label">Review Comment</span>
                    <span className="detail-value">{r.reviewComment}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {status === 'pending' && (
            <div className="detail-actions">
              <button
                className="detail-btn-success"
                onClick={async () => { if (await handleApprove(r)) setSelectedRequest(null); }}
              >
                ✓ Approve Change
              </button>
              <button
                className="detail-btn-danger"
                onClick={async () => { if (await handleReject(r)) setSelectedRequest(null); }}
              >
                ✕ Reject Request
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-approvals-view">
      <div className="view-header">
        <h2>Grade Approvals</h2>
        {pendingCount > 0 && (
          <span className="pending-badge">{pendingCount} pending</span>
        )}
      </div>
      <StatCards items={[['Pending',apprStats.p],['Approved',apprStats.a],['Rejected',apprStats.r],['Total',requests.length],['Students',apprStats.students],['Classes',apprStats.classes]]} charts={[{title:'Approval Status',type:'pie',data:[{name:'Pending',value:apprStats.p,color:'#ea580c'},{name:'Approved',value:apprStats.a,color:'#16a34a'},{name:'Rejected',value:apprStats.r,color:'#dc2626'}]}]} />

      <div className="filter-bar">
        <button
          className={`filter-button ${selectedFilter === 'pending' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('pending')}
        >
          Pending ({requests.filter(r => normalizeStatus(r.status) === 'pending').length})
        </button>
        <button
          className={`filter-button ${selectedFilter === 'approved' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('approved')}
        >
          Approved ({requests.filter(r => normalizeStatus(r.status) === 'approved').length})
        </button>
        <button
          className={`filter-button ${selectedFilter === 'rejected' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('rejected')}
        >
          Rejected ({requests.filter(r => normalizeStatus(r.status) === 'rejected').length})
        </button>
        <button
          className={`filter-button ${selectedFilter === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedFilter('all')}
        >
          All ({requests.length})
        </button>
      </div>

      {filteredRequests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✅</div>
          <h3>No Requests</h3>
          <p>No grade change requests found</p>
        </div>
      ) : (
        <div className="requests-list">
          {filteredRequests.map(request => {
            const originalPct = getPercentage(request.originalScore, request.originalMaxScore);
            const newPct = getPercentage(request.newScore, request.newMaxScore);
            const originalLetter = getLetterGrade(originalPct);
            const newLetter = getLetterGrade(newPct);
            return (
              <div
                key={request.id}
                className="request-card"
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedRequest(request)}
              >
                <div className="request-info">
                  <h4>Grade Change Request</h4>
                  <p><strong>Class:</strong> {request.className || 'N/A'}</p>
                  <p><strong>Assignment:</strong> {request.assignmentName || 'N/A'}</p>
                  <p><strong>Student:</strong> {request.studentName || request.studentId || 'N/A'}</p>
                  <p>
                    <strong>Score:</strong> {request.originalScore}/{request.originalMaxScore} ({originalPct.toFixed(1)}% {originalLetter})
                    {' → '}
                    {request.newScore}/{request.newMaxScore} ({newPct.toFixed(1)}% {newLetter})
                  </p>
                  <p><strong>Reason:</strong> {request.reason || 'No reason provided'}</p>
                  <p><strong>Requested:</strong> {formatDate(request.requestedAt)}</p>
                </div>
                <div className="request-status">
                  <span
                    className="status-badge"
                    style={{ background: `${getStatusColor(request.status)}20`, color: getStatusColor(request.status) }}
                  >
                    {normalizeStatus(request.status)}
                  </span>
                </div>
                {normalizeStatus(request.status) === 'pending' && (
                  <div className="request-actions">
                    <button
                      className="approve-button"
                      onClick={(e) => { e.stopPropagation(); handleApprove(request); }}
                    >
                      Approve
                    </button>
                    <button
                      className="reject-button"
                      onClick={(e) => { e.stopPropagation(); handleReject(request); }}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdminApprovalsView;
